import { translationKey } from '../../i18n';
import { MAX_DAILY_REMINDER_TIMES } from './MedicationTimesEditor';

const validInteger = (value, max) => Number.isInteger(value) && value > 0 && value <= max;

export function readPhotoSchedule(med = {}) {
    const durationDays = !med.durationInvalid && validInteger(med.durationDays, 365) ? med.durationDays : null;
    const rawTimes = Array.isArray(med.suggestedTimes) ? med.suggestedTimes : [];
    const count = validInteger(med.timesPerDay, MAX_DAILY_REMINDER_TIMES) ? med.timesPerDay : null;
    const invalid = med.timesInvalid || (med.timesPerDay != null && count == null)
        || rawTimes.length > MAX_DAILY_REMINDER_TIMES
        || rawTimes.some(time => !/^([01]\d|2[0-3]):[0-5]\d$/.test(time))
        || new Set(rawTimes).size !== rawTimes.length
        || (count != null && rawTimes.length > 0 && count !== rawTimes.length);
    const defaults = {
        1: ['08:00'], 2: ['08:00', '18:00'], 3: ['08:00', '12:00', '18:00'],
        4: ['08:00', '12:00', '18:00', '22:00'],
        5: ['08:00', '11:00', '14:00', '18:00', '22:00'],
        6: ['08:00', '10:00', '12:00', '15:00', '18:00', '22:00'],
    };
    const times = invalid ? [] : (rawTimes.length ? [...rawTimes].sort() : (defaults[count] || []));
    return {
        durationDays,
        times,
        timesSource: times.length === 0 ? 'UNKNOWN'
            : (med.timesSource === 'PRINTED' && rawTimes.length > 0 ? 'PRINTED' : 'PROPOSED'),
    };
}

export function mergePhotoSchedule(med, common = {}) {
    const own = readPhotoSchedule(med);
    const durationInvalid = med.durationInvalid || (med.durationDays != null && own.durationDays == null);
    const timesInvalid = med.timesInvalid || ((med.timesPerDay != null || med.suggestedTimes?.length > 0) && !own.times.length);
    return {
        durationDays: durationInvalid ? null : (own.durationDays ?? common.durationDays ?? null),
        times: timesInvalid ? [] : (own.times.length ? own.times : (common.times || [])),
        timesSource: timesInvalid ? 'UNKNOWN' : (own.times.length ? own.timesSource : (common.timesSource || 'UNKNOWN')),
    };
}

// Only unambiguous, supported instructions become an editable recurring schedule.
export function photoRegimenSchedule(text = '') {
    const instruction = (text || '').trim();
    const optionalDose = translationKey('(?:\\d+(?:\\.\\d+)?\\s*(?:정|알)\\s*)?');
    if (new RegExp(`^${optionalDose}격일(?:\\s*복용)?$`).test(instruction)) {
        return { frequencyType: 'EVERY_OTHER_DAY' };
    }
    const interval = instruction.match(new RegExp(`^${optionalDose}(\\d+)일마다(?:\\s*(?:한 번|1회))?(?:\\s*복용)?$`));
    if (interval && Number(interval[1]) >= 2 && Number(interval[1]) <= 365) {
        return { frequencyType: 'EVERY_N_DAYS', intervalDays: Number(interval[1]) };
    }
    if (/^(필요시|필요할 때)(?:\s*복용)?$/.test(instruction)) return { frequencyType: 'AS_NEEDED' };
    return {};
}

export function photoScheduleSetup(meds) {
    const periodKeys = meds.filter(med => med.schedule?.durationDays > 0).map(med => med.key);
    const reminderKeys = meds.filter(med => med.schedule?.times?.length > 0).map(med => med.key);
    const periods = new Set(meds.map(med => med.schedule?.durationDays ?? null));
    const reminders = new Set(meds.map(med => (med.schedule?.times || []).join(',')));
    return {
        periodKeys,
        reminderKeys,
        perMedicationPeriod: meds.length > 1 && periods.size > 1,
        perMedicationReminder: meds.length > 1 && reminders.size > 1,
    };
}
